import { useEffect, useRef, useState } from "react";
import { Dialog, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

interface GallerySlideshowProps {
  open: boolean;
  images: any[];
  startIndex: number;
  backgroundPairs: Record<number, any>;
  activeVersions: Record<number, "original" | "background">;
  onClose: () => void;
}

const frostedCircle: React.CSSProperties = {
  backgroundColor: "rgba(255,255,255,0.12)",
  borderRadius: "50%",
  color: "rgba(255,255,255,0.85)",
};

export default function GallerySlideshow({
  open,
  images,
  startIndex,
  backgroundPairs,
  activeVersions,
  onClose,
}: GallerySlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const touchStartX = useRef<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  useEffect(() => {
    if (open) setCurrentIndex(startIndex);
  }, [open, startIndex]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") setCurrentIndex((i) => (i - 1 + images.length) % images.length);
      else if (e.key === "ArrowRight") setCurrentIndex((i) => (i + 1) % images.length);
      else if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, images.length, onClose]);

  if (!images.length) return null;

  const image = images[currentIndex];
  const activeVersion = activeVersions[image?.id] ?? "original";
  const backgroundImage = backgroundPairs[image?.id];
  const displayImage = activeVersion === "background" && backgroundImage ? backgroundImage : image;
  const src = `/api/${displayImage?.file}`;

  function prev() { setCurrentIndex((i) => (i - 1 + images.length) % images.length); }
  function next() { setCurrentIndex((i) => (i + 1) % images.length); }

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > 50) deltaX < 0 ? next() : prev();
    touchStartX.current = null;
  }

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            position: "relative",
          },
        }}
      >
        {/* Blurred background */}
        <div style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${src})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(24px)",
          transform: "scale(1.08)",
          zIndex: 0,
        }} />
        {/* Dark overlay */}
        <div style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.45)",
          zIndex: 1,
        }} />

        {/* Close */}
        <IconButton
          onClick={onClose}
          style={{ ...frostedCircle, position: "absolute", top: 12, right: 12, zIndex: 10 }}
        >
          <CloseIcon />
        </IconButton>

        {/* Image — centred in remaining space */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            padding: "56px 16px 0",
            position: "relative",
            zIndex: 2,
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <img
            src={src}
            alt=""
            style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
          />
        </div>

        {/* Bottom bar — pinned, never shifts */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 32px",
            paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
            position: "relative",
            zIndex: 2,
          }}
        >
          <IconButton onClick={prev} style={frostedCircle}>
            <ChevronLeftIcon style={{ fontSize: 28 }} />
          </IconButton>
          <Typography style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
            {currentIndex + 1} / {images.length}
          </Typography>
          <IconButton onClick={next} style={frostedCircle}>
            <ChevronRightIcon style={{ fontSize: 28 }} />
          </IconButton>
        </div>
      </Dialog>
    );
  }

  // Tablet + desktop
  const arrowIconSize = isTablet ? 32 : 40;
  const imagePadding = isTablet ? "48px 40px 32px" : "48px 80px 32px";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isTablet ? "lg" : "xl"}
      fullWidth
      PaperProps={{ style: { backgroundColor: "transparent", boxShadow: "none" } }}
    >
      {/* Close */}
      <IconButton
        onClick={onClose}
        style={{ ...frostedCircle, position: "absolute", top: 8, right: 8, zIndex: 10 }}
      >
        <CloseIcon />
      </IconButton>

      {/* Prev */}
      <IconButton
        onClick={prev}
        style={{ ...frostedCircle, position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
      >
        <ChevronLeftIcon style={{ fontSize: arrowIconSize }} />
      </IconButton>

      {/* Image */}
      <div style={{ textAlign: "center", padding: imagePadding }}>
        <img
          src={src}
          alt=""
          style={{ maxHeight: "75vh", maxWidth: "100%", objectFit: "contain", display: "block", margin: "0 auto" }}
        />
        <Typography style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginTop: 12 }}>
          {currentIndex + 1} / {images.length}
        </Typography>
      </div>

      {/* Next */}
      <IconButton
        onClick={next}
        style={{ ...frostedCircle, position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
      >
        <ChevronRightIcon style={{ fontSize: arrowIconSize }} />
      </IconButton>
    </Dialog>
  );
}
